/**
 * Body Insight Progress Indicator
 *
 * Displays progress through the 5-page body insight flow.
 * Feature: 017-body-insight-summary
 */

import React from 'react';

interface BodyInsightProgressProps {
  currentPage: number; // 1-5
  totalPages: number; // Always 5
}

export const BodyInsightProgress: React.FC<BodyInsightProgressProps> = ({
  currentPage,
  totalPages
}) => {
  const progressPercentage = (currentPage / totalPages) * 100;

  return (
    <div className="w-full mb-6">
      {/* Progress text */}
      <div className="flex justify-between items-center mb-2">
        <span className="text-sm font-medium text-gray-600">
          Page {currentPage} of {totalPages}
        </span>
        <span className="text-sm font-medium text-gray-600">
          {Math.round(progressPercentage)}%
        </span>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
        <div
          className="bg-gradient-to-r from-[#C98CCC] to-[#B7A76D] h-2 rounded-full transition-all duration-300 ease-in-out"
          style={{ width: `${progressPercentage}%` }}
          role="progressbar"
          aria-valuenow={currentPage}
          aria-valuemin={1}
          aria-valuemax={totalPages}
          aria-label={`Progress: ${currentPage} of ${totalPages} pages`}
        />
      </div>

      {/* Page dots (optional visual indicator) */}
      <div className="flex justify-center gap-2 mt-3">
        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
          <div
            key={page}
            className={`w-2 h-2 rounded-full transition-colors duration-200 ${
              page <= currentPage
                ? 'bg-[#C98CCC]'
                : 'bg-gray-300'
            }`}
            aria-hidden="true"
          />
        ))}
      </div>
    </div>
  );
};
